import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider, keepPreviousData } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostHogConfig, posthog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { StrictMode, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';

import { loadContext } from 'src/api/loaders';
import type { User } from 'src/api/models';
import { router } from 'src/routes/router.tsx';

import './app.css';

// Disable sentry replay capture as we are capturing using posthog too
// async function loadSentryReplay() {
//   const { Replay } = await import('@sentry/react');
//   Sentry.addIntegration(new Replay());
// }

function InnerApp() {
  useEffect(() => {
    const options: Partial<PostHogConfig> = {
      api_host: 'https://app.posthog.com',
      disable_session_recording: true,
      session_recording: {
        recordCrossOriginIframes: true,
      },
      loaded: (posthog) => {
        const enabled = Math.floor(Math.random() * 100);
        // Enable for 1 in 100 or if path is video-embed
        if (enabled === 0 || window.location.pathname.includes('/videos-embed')) {
          posthog.startSessionRecording();
        }
      },
    };
    const context = loadContext() as {
      posthogApiKey: string;
      user: User | undefined;
    };
    posthog.init(context.posthogApiKey, options);
    if (context.user) {
      posthog.identify(context.user.id.toString(), {
        email: context.user.email,
        firstName: context.user.first_name,
        username: context.user.username,
      });
    }
    // loadSentryReplay();
  }, []);

  const [queryClient] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: Infinity,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          placeholderData: keepPreviousData,
        },
      },
    }),
  );

  return (
    <StrictMode>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router(queryClient)} />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>
  );
}

export const App = Sentry.withProfiler(InnerApp);
